import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Blocks, Play, FlaskConical, BookmarkPlus, CheckCircle2, XCircle, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BlocksWorkspace } from "../components/blocks/BlocksWorkspace";
import { saveBlocksSolution, solutionStatus } from "../components/blocks/solutionTools";
import { describeInputs } from "../components/admin/BlocksInputsEditor";
import { NavPlayback } from "../components/nav/NavPlayback";
import { countNavBlocks } from "../components/blocks/navBlocks";
import { clientHeaders } from "../lib/clientId";
import { clearDraft } from "../lib/blocksDraft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin: build a Blocks question's sample solution with blocks, test it against the same test cases as Python, save it.
export default function AdminBlocksSolution({ user }) {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [code, setCode] = useState("");
  const [workspace, setWorkspace] = useState(null);
  const [initialWorkspace, setInitialWorkspace] = useState(undefined);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runOutput, setRunOutput] = useState(null);
  const [results, setResults] = useState(null);
  const [converting, setConverting] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const draftKey = `admin_solution_${questionId}`;

  useEffect(() => {
    if (!user) return;
    if (!user.is_admin) { navigate("/"); return; }
    fetch(`${API}/admin/questions/${questionId}`, { credentials: "include" })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Question not found"); return r.json(); })
      .then((q) => {
        if (q.mode !== "blocks" && q.mode !== "junior") throw new Error("This is a Python question - block solutions only apply to Blocks and Junior Blocks questions");
        setQuestion(q);
        setInitialWorkspace(q.blocks?.solution_workspace || null);
      })
      .catch((e) => setLoadError(e.message));
  }, [questionId, user, navigate]);

  const onChange = useCallback((py, state) => { setCode(py); setWorkspace(state); }, []);
  const status = useMemo(() => solutionStatus(question), [question]);
  const tests = question?.test_cases || [];
  const isNav = question?.mode === "junior" || question?.blocks?.kind === "nav";
  const nav = question?.blocks?.nav;
  const hasPython = !!(question?.sample_solution || question?.starter_code || "").trim();
  const navUsed = isNav ? countNavBlocks(workspace) : 0;
  const navProblem = isNav && nav?.max_blocks && navUsed > nav.max_blocks ? `Too many blocks: ${navUsed} used, ${nav.max_blocks} allowed (loops, ifs and function definitions count as 2)` : null;

  const runNav = useCallback(async () => {
    const r = await fetch(`${API}/code/nav-run`, { method: "POST", headers: clientHeaders(), credentials: "include", body: JSON.stringify({ question_id: questionId, code, workspace }) });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail?.message || d.detail || "Run failed");
    return d;
  }, [questionId, code, workspace]);

  const runExample = async () => {
    setRunning(true);
    setRunOutput(null);
    try {
      const r = await fetch(`${API}/code/test`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ code, custom_input: question.example_input || "", mode: "blocks" }) });
      const d = await r.json();
      setRunOutput(d.success ? { ok: true, text: d.output } : { ok: false, text: d.error || d.detail || "Error" });
    } catch (e) {
      setRunOutput({ ok: false, text: e.message });
    } finally {
      setRunning(false);
    }
  };

  const testAll = async () => {
    setTesting(true);
    setResults(null);
    try {
      const r = await fetch(`${API}/admin/validate-solution`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ code, test_cases: tests, mode: "blocks" }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Validation failed");
      setResults(d.results);
      d.valid ? toast.success(`All ${d.passed}/${d.total} tests passed`) : toast.error(`${d.passed}/${d.total} tests passed`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!workspace) return;
    setSaving(true);
    try {
      const res = await saveBlocksSolution(questionId, workspace, code, "manual");
      if (res.status === "verified") {
        setQuestion(res.question);
        setResults(null);
        clearDraft(draftKey, user?.user_id);
        toast.success("Saved as the block sample solution");
      } else {
        if (res.results) setResults(res.results);
        toast.error(res.reason);
      }
    } finally {
      setSaving(false);
    }
  };

  const loadAuto = async () => {
    setConverting(true);
    try {
      const r = await fetch(`${API}/admin/questions/${questionId}/blocks-solution/auto`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Could not convert the Python solution"); return; }
      clearDraft(draftKey, user?.user_id);
      setInitialWorkspace(d.workspace);
      setEditorKey((k) => k + 1);
      setResults(null);
      toast.success("Loaded the auto-converted blocks - test and save them");
    } finally {
      setConverting(false);
    }
  };

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="blocks-solution-builder-error">
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-3" />
        <p className="mb-4">{loadError}</p>
        <Button variant="outline" onClick={() => navigate("/admin")}>Back to admin</Button>
      </div>
    );
  }
  if (!question || initialWorkspace === undefined) {
    return <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading question…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="blocks-solution-builder">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/admin")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2" data-testid="blocks-solution-back-btn">
            <ArrowLeft className="w-4 h-4" /> Blocks questions
          </button>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2"><Blocks className="w-6 h-6 text-sky-500" /> {question.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isNav ? "Build a drill program with blocks and press Play. It must collect every goal on the grid (within this question's block rules) before it can be saved." : `Build the sample solution with blocks. It is tested against the same ${tests.length} test case${tests.length === 1 ? "" : "s"} as a Python solution before it can be saved.`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="blocks-solution-status">
          <Badge variant="outline">{question.pool === "challenge" ? "Challenge" : "Warm Up"}</Badge>
          <Badge variant="outline" className="font-mono text-xs">{isNav ? "navigation grid" : describeInputs(question.blocks?.inputs)}</Badge>
          {status.state === "verified" ? (
            <Badge className="gap-1 bg-emerald-500/15 text-emerald-600 border-emerald-400" variant="outline"><CheckCircle2 className="w-3 h-3" /> Saved ({status.source === "auto" ? "auto" : "manual"})</Badge>
          ) : (
            <Badge className="gap-1 bg-amber-500/15 text-amber-600 border-amber-400" variant="outline" title={status.note}><AlertTriangle className="w-3 h-3" /> No block solution yet</Badge>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-5">
        <div className="space-y-4">
          <div className="bg-card rounded-2xl border border-border/40 p-5 space-y-3" data-testid="blocks-solution-question">
            {!isNav && <p className="text-sm whitespace-pre-wrap">{question.description}</p>}
            {isNav && <p className="text-sm text-muted-foreground">Students see the grid first, with only the rules this grid uses under "How it works".</p>}
            {!isNav && question.input_format && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Input:</span> {question.input_format}</p>}
            {!isNav && question.output_format && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Output:</span> {question.output_format}</p>}
            {!isNav && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><p className="text-muted-foreground mb-1">Example input</p><pre className="bg-muted rounded p-2 whitespace-pre-wrap min-h-[2rem]">{question.example_input || "(none)"}</pre></div>
              <div><p className="text-muted-foreground mb-1">Example output</p><pre className="bg-muted rounded p-2 whitespace-pre-wrap min-h-[2rem]">{question.example_output || "(none)"}</pre></div>
            </div>
            )}
            {status.note && status.state !== "verified" && <p className="text-xs text-amber-600" data-testid="blocks-solution-note">{status.note}</p>}
          </div>

          {isNav ? (
            <div className="bg-card rounded-2xl border border-border/40 p-5 space-y-2" data-testid="blocks-solution-nav">
              <h2 className="font-semibold">Drill run</h2>
              <NavPlayback nav={nav} runProgram={runNav} disabled={!code || !!navProblem} disabledReason={navProblem} compact />
              {results && <p className={`text-xs font-medium ${results[0]?.passed ? "text-emerald-600" : "text-destructive"}`} data-testid="blocks-solution-tests-summary">{results[0]?.message}</p>}
            </div>
          ) : (
          <div className="bg-card rounded-2xl border border-border/40 p-5" data-testid="blocks-solution-tests">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Test cases ({tests.length})</h2>
              {results && (
                <span className="text-xs font-medium" data-testid="blocks-solution-tests-summary">
                  {results.filter((r) => r.passed).length}/{results.length} passed
                </span>
              )}
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {tests.map((tc, i) => {
                const res = results?.[i];
                return (
                  <div key={i} className={`rounded-lg border p-2.5 text-xs ${res ? (res.passed ? "border-emerald-400/60 bg-emerald-500/5" : "border-destructive/50 bg-destructive/5") : "border-border"}`} data-testid={`blocks-solution-test-${i + 1}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      {res ? (res.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-destructive" />) : <span className="w-3.5 h-3.5 rounded-full border border-border inline-block" />}
                      <span className="font-medium">Test {i + 1}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-muted-foreground">Input</p><pre className="whitespace-pre-wrap font-mono">{tc.input || "(empty)"}</pre></div>
                      <div><p className="text-muted-foreground">Expected</p><pre className="whitespace-pre-wrap font-mono">{tc.expected_output}</pre></div>
                    </div>
                    {res && !res.passed && (
                      <p className="mt-1.5 text-destructive whitespace-pre-wrap font-mono">{res.error ? res.error.split("\n").filter(Boolean).pop() : `Got: ${res.actual === "" ? "(nothing)" : res.actual}`}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        <div className="space-y-3">
          <BlocksWorkspace
            key={editorKey}
            questionId={question.question_id}
            storageId={draftKey}
            inputs={question.blocks?.inputs || []}
            rules={question.blocks || null}
            initialWorkspace={initialWorkspace || undefined}
            height={480}
            onChange={onChange}
            variant={isNav ? "nav" : "program"}
            navRules={isNav ? nav : null}
            hidePythonPanel={isNav}
          />
          {isNav && <p className="text-xs text-muted-foreground" data-testid="blocks-solution-nav-count">{navUsed}{nav?.max_blocks ? ` / ${nav.max_blocks}` : ""} blocks used</p>}
          <div className="flex items-center gap-2 flex-wrap" data-testid="blocks-solution-actions">
            {!isNav && (
            <Button variant="outline" onClick={runExample} disabled={running || !code} className="gap-2" data-testid="blocks-solution-run-btn">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run with example input
            </Button>
            )}
            {!isNav && (
            <Button variant="outline" onClick={testAll} disabled={testing || !code || !tests.length} className="gap-2" data-testid="blocks-solution-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Test against all {tests.length} test cases
            </Button>
            )}
            <Button onClick={save} disabled={saving || !code || !!navProblem} className="gap-2 rounded-xl" data-testid="blocks-solution-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />} {isNav ? "Save as the drill solution" : "Save as sample solution"}
            </Button>
            {!isNav && hasPython && (
            <Button variant="ghost" onClick={loadAuto} disabled={converting} className="gap-2" title="Replace the workspace with the auto-converted Python solution" data-testid="blocks-solution-load-auto-btn">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Load auto-converted
            </Button>
            )}
          </div>
          {runOutput && (
            <div className={`rounded-xl border p-3 text-sm font-mono whitespace-pre-wrap ${runOutput.ok ? "border-border bg-muted/40" : "border-destructive/50 bg-destructive/5 text-destructive"}`} data-testid="blocks-solution-run-output">
              {runOutput.text || "(no output)"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
